import { useEffect, useState } from "react";
import { ScreenStyles } from "./LocationScreen";
import { useJourney } from "./JourneyProvider";
import { validateLiveness } from "../../services/allowService";

interface FaceScreenProps {
  styles: ScreenStyles;
  onNext?: () => void;
}

export default function FaceScreen({ styles, onNext }: FaceScreenProps) {
  const { cpf, ip, journey, setSelfieImage } = useJourney();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let styleTag: HTMLStyleElement | null = null;

    const loadSdk = () =>
      new Promise<void>((resolve, reject) => {
        if ((window as any).SerasaExperianIDF) return resolve();
        const script = document.createElement("script");
        script.src =
          "https://stage-sdk-js.allowme.com.br/serasaexperianidf.min.js";
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Falha ao carregar o SDK"));
        document.body.appendChild(script);
      });

    const customOptions = {
      wizard: {
        enabled: true,
        titleColor: styles.textColor,
        titleAlign: "left",
        buttonCloseEnabled: true,
        buttonTextColor: styles.buttonLabelColor,
        buttonBackgroundNormalColor: styles.buttonColor,
        buttonBackgroundHighlightColor: styles.buttonColor,
        buttonBackgroundDisabledColor: "#5e6976",
        instructionsScreen: {
          logoBackgroundColor: styles.backgroundColor,
          iconBackgroundColor: styles.buttonColor,
          itemColor: styles.textColor,
        },
      },
      faceTecCustomization: {
        loadingBackgroundColor: styles.backgroundColor,
        feedbackBackgroundColor: "rgba(45,53,73,0.9)",
        successBackgroundColor: "#09DEA1",
        buttonNormalColor: styles.buttonColor,
        buttonHighlightColor: styles.buttonColor,
        buttonDisabledColor: "#cccccc",
        buttonTextNormalColor: styles.buttonLabelColor,
        buttonTextHighlightColor: styles.buttonLabelColor,
        buttonTextDisabledColor: styles.buttonLabelColor,
        guidanceForegroundColor: styles.textColor,
        vocalGuidanceEnabled: true,
      },
    };

    const init = async () => {
      setError(null);

      try {
        await loadSdk();
        if (cancelled) return;

        styleTag = document.createElement("style");
        styleTag.setAttribute("data-sdk-bg", "true");
        styleTag.innerHTML = `
          #serasa-instruction-screen-id,
          #serasa-instruction-screen-id main#instruction-screen {
            background-color: ${styles.backgroundColor} !important;
          }
        `;
        document.head.appendChild(styleTag);

        const sdk: any = (window as any).SerasaExperianIDF;
        await sdk.setupLiveness({ options: customOptions, callback: () => {} });
        if (cancelled) return;

        const result = await sdk.startLiveness();
        if (result?.error) {
          // mensagem do SDK (se disponível)
          throw new Error(
            result.error?.detail || "Falha na sessão de liveness."
          );
        }

        setLoading(true);
        const { data } = await validateLiveness({
          integrationName: "onboarding",
          userId: cpf ?? "",
          userIp: ip || undefined,
          event: result.event || result.payload,
          cpf: (cpf ?? "").replace(/\D/g, ""),
          name: journey?.nomeCliente || "",
          images: result.images,
          protocol: journey?.protocol,
        });

        if (result.images && result.images.length > 0) {
          setSelfieImage(result.images[0]);
        }
        setLoading(false);

        console.log("Resposta backend:", data);

        if (!cancelled) onNext?.();
      } catch (err: any) {
        console.error(err);
        if (!cancelled) setError(err?.message ?? "Erro ao validar liveness.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();

    return () => {
      cancelled = true;
      if (styleTag) styleTag.remove();
    };
  }, [
    styles.backgroundColor,
    styles.textColor,
    styles.buttonColor,
    styles.buttonLabelColor,
    onNext,
    cpf,
    ip,
    journey?.nomeCliente,
  ]);

  // UI mínima: overlay de loading + mensagem de erro opcional
  return (
    <>
      {loading && (
        <div
          role="status"
          aria-busy="true"
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.35)",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: styles.backgroundColor,
              color: styles.textColor,
              padding: "16px 20px",
              borderRadius: 8,
              boxShadow: "0 6px 18px rgba(0,0,0,.2)",
              minWidth: 220,
              textAlign: "center",
              fontWeight: 600,
            }}
          >
            Validando biometria...
          </div>
        </div>
      )}

      {!loading && error && (
        <div
          style={{
            color: "#fff",
            background: "rgba(220, 0, 0, .8)",
            padding: 12,
            borderRadius: 8,
            position: "fixed",
            left: 16,
            right: 16,
            bottom: 16,
            zIndex: 10000,
            textAlign: "center",
            fontWeight: 600,
          }}
        >
          {error}
        </div>
      )}
    </>
  );
}
